import { initializeDropdown } from "./dropdown.js";
import {
  showToast,
  showGlobalPreloader,
  hideGlobalPreloader,
  showCustomConfirm,
} from "./utils.js";

document.addEventListener("DOMContentLoaded", () => {
  const currencySelectDropdown = document.getElementById(
    "currency-select-dropdown",
  );
  const currencySelect = document.getElementById("currency-select");

  // Helper to get the current plan info div reliably
  const getCurrentPlanInfoDiv = () =>
    document.getElementById("current-plan-info");

  // Hardcoded plans data for immediate rendering (Synchronized with pricing_plan.md)
  const PLANS_DATA = {
    free: {
      name: "Free (Starter)",
      monthly: { INR: 0, USD: 0 },
      yearly: { INR: 0, USD: 0 },
      features: {
        max_members: 10,
        support: "Email",
        admins: 1,
        perks: ["Basic Attendance (Manual)", "Profile Management"]
      },
    },
    basic: {
      name: "Basic",
      monthly: { INR: 92900, USD: 1200 },
      yearly: {
        INR: 929900,
        USD: 12000,
        discount_text: "Two months free!",
      },
      features: {
        max_members: 350,
        support: "App Notifications",
        admins: 3,
        perks: ["QR Code Attendance", "AI Timetable Engine", "Basic ERP Tools", "30-day Analytics"]
      },
    },
    premium: {
      name: "Premium (Elite)",
      monthly: { INR: 299900, USD: 3900 },
      yearly: {
        INR: 2999000,
        USD: 39000,
        discount_text: "Two months free!",
      },
      features: {
        max_members: 1500,
        support: "Priority 24/7",
        admins: 10,
        perks: ["Full ERP Suite", "Advanced AI Analytics", "Automated Daily PDF Reports"]
      },
    },
  };

  if (currencySelectDropdown) {
    initializeDropdown(currencySelectDropdown);
    currencySelectDropdown.addEventListener("dropdown-change", (e) => {
      const val = e.detail.value;
      if (currencySelect) currencySelect.value = val;
      renderPricingCards();
    });
  }

  // Function to get plans array from hardcoded data
  function getPlansArray() {
    const plansData = PLANS_DATA;
    return Object.keys(plansData).map((planId) => {
      const plan = plansData[planId];
      const featuresArray = [];
      
      featuresArray.push({
        name: "Max Members",
        description: `Up to ${plan.features.max_members} total members`,
      });
      
      featuresArray.push({
        name: "Admin Accounts",
        description: `Up to ${plan.features.admins} Admin account(s)`,
      });

      featuresArray.push({
        name: "Support",
        description: plan.features.support,
      });

      if (plan.features.perks) {
        plan.features.perks.forEach(perk => {
          featuresArray.push({
            name: perk,
            description: "Included",
          });
        });
      }

      return {
        id: planId,
        name: plan.name,
        monthly: plan.monthly,
        yearly: plan.yearly,
        features: featuresArray,
      };
    }).concat([{
      id: "enterprise",
      name: "Enterprise (Custom)",
      monthly: { INR: 0, USD: 0 },
      yearly: { INR: 0, USD: 0 },
      features: [
        { name: "Custom Capacity", description: "Negotiated limits" },
        { name: "Custom ERP", description: "Tailored to your needs" },
        { name: "Direct Line", description: "Talk to Chairman" },
        { name: "Full Control", description: "White-label options" },
      ],
    }]);
  }

  // Function to fetch user's current plan
  async function fetchUserPlan() {
    const token = localStorage.getItem("access_token");
    if (!token) return null;
    try {
      const response = await fetch("/api/user/plan", {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Background-Request": "true",
        },
      });
      if (response.ok) return await response.json();
      if (response.status === 404) return { name: "Free", id: "free" };
      return null;
    } catch {
      return null;
    }
  }

  // Function to display user's current plan information
  function displayCurrentPlan(userPlan) {
    const currentPlanInfoDiv = getCurrentPlanInfoDiv();
    if (!currentPlanInfoDiv) return;

    if (userPlan) {
      currentPlanInfoDiv.innerHTML = `<p style="text-align:center; color:var(--accent-green); font-weight:bold; font-size:1.1rem;">Active Plan: <span style="color:#00f2ff;">${userPlan.name}</span></p>`;
    } else {
      currentPlanInfoDiv.innerHTML = "";
    }
  }

  let cachedUserPlan = null;

  // Function to render pricing cards
  function renderPricingCards() {
    try {
      const plans = getPlansArray();
      const selectedCurrency = currencySelect ? currencySelect.value : "INR";

      const freePlanRow = document.getElementById("free-plan-row");
      const basicPlansRow = document.getElementById("basic-plans-row");
      const premiumPlansRow = document.getElementById("premium-plans-row");
      const enterprisePlansRow = document.getElementById(
        "enterprise-plans-row",
      );

      [freePlanRow, basicPlansRow, premiumPlansRow, enterprisePlansRow].forEach(
        (row) => {
          if (row) row.innerHTML = "";
        },
      );

      // Display current plan from cache if available
      displayCurrentPlan(cachedUserPlan);

      const renderCard = (plan, cycle, row) => {
        if (!row) return;
        const card = createPricingCard(
          plan,
          cycle,
          cachedUserPlan,
          selectedCurrency,
        );
        row.appendChild(card);
      };

      const freePlan = plans.find((p) => p.id === "free");
      if (freePlan) renderCard(freePlan, "monthly", freePlanRow);

      const basicPlan = plans.find((p) => p.id === "basic");
      if (basicPlan) {
        renderCard(basicPlan, "monthly", basicPlansRow);
        renderCard(basicPlan, "yearly", basicPlansRow);
      }

      const premiumPlan = plans.find((p) => p.id === "premium");
      if (premiumPlan) {
        renderCard(premiumPlan, "monthly", premiumPlansRow);
        renderCard(premiumPlan, "yearly", premiumPlansRow);
      }

      const enterprisePlan = plans.find((p) => p.id === "enterprise");
      if (enterprisePlan)
        renderCard(enterprisePlan, "monthly", enterprisePlansRow);

      // Add event listeners to "Choose Plan" buttons
      document.querySelectorAll(".pricing-card .btn").forEach((button) => {
        button.addEventListener("click", handleChoosePlan);
      });
    } catch {
      // Failed to render pricing cards
    }
  }

  // New Background loader for user plan
  async function updatePlanDataInBackground() {
    const userPlan = await fetchUserPlan();
    if (userPlan) {
      cachedUserPlan = userPlan;
      renderPricingCards(); // Fast re-render with user context
    }
  }

  // Helper function to create a single pricing card element
  function createPricingCard(plan, billingCycle, userPlan, selectedCurrency) {
    const userCycle =
      userPlan && userPlan.billing_cycle
        ? userPlan.billing_cycle.toLowerCase()
        : "";
    const cardCycle = billingCycle.toLowerCase();
    
    // Tier levels for comparison
    const tiers = { free: 0, basic: 1, premium: 2, enterprise: 3 };
    const currentTier = userPlan ? (tiers[userPlan.id.toLowerCase()] || 0) : -1;
    const cardTier = tiers[plan.id.toLowerCase()] || 0;

    const isCurrentPlan =
      userPlan &&
      userPlan.id.toLowerCase() === plan.id.toLowerCase() &&
      (plan.id === "free" || userCycle === cardCycle || (plan.id === "free" && !userCycle));

    // Disable if it's the current plan OR if it's a smaller tier (no manual downgrade)
    // Exception: Enterprise button is always enabled for contact
    const isSmallerTier = cardTier < currentTier && plan.id !== "enterprise";
    const shouldDisable = isCurrentPlan || isSmallerTier;

    const card = document.createElement("div");
    card.className = `pricing-card ${isCurrentPlan ? "current-plan" : ""} ${plan.id === "enterprise" ? "recommended" : ""}`;

    let priceHtml;
    let discountText = "";
    if (plan.id === "enterprise") {
      priceHtml = `<p class="price">Custom</p>`;
    } else {
      const priceData = plan[billingCycle];
      let displayPrice;
      let currencySymbol;
      if (selectedCurrency === "USD") {
        displayPrice = (priceData.USD / 100).toFixed(2);
        currencySymbol = "$";
      } else {
        displayPrice = (priceData.INR / 100).toFixed(0);
        currencySymbol = "₹";
      }
      const pricePeriod = billingCycle === "monthly" ? "/mo" : "/yr";
      discountText = priceData.discount_text
        ? `<p class="discount-text">${priceData.discount_text}</p>`
        : "";
      priceHtml = `<p class="price">${currencySymbol}${displayPrice}<span>${pricePeriod}</span></p>`;
    }

    let featuresHtml = "";
    plan.features.forEach((feature) => {
      let iconClass = "fa-check-circle";
      const name = feature.name.toLowerCase();
      if (name.includes("staff")) iconClass = "fa-user-tie";
      else if (name.includes("learner") || name.includes("client"))
        iconClass = "fa-user-graduate";
      else if (name.includes("member")) iconClass = "fa-users";
      else if (name.includes("support")) iconClass = "fa-headset";
      else if (name.includes("report")) iconClass = "fa-file-chart-line";
      else if (name.includes("notification")) iconClass = "fa-bell";
      else if (name.includes("onboarding")) iconClass = "fa-rocket";

      featuresHtml += `<li><i class="fas ${iconClass} feature-list-icon"></i><div class="feature-info"><span class="feature-name">${feature.name}</span><span class="feature-description">${feature.description}</span></div></li>`;
    });

    const headerCycle =
      plan.id === "enterprise"
        ? "Enterprise"
        : billingCycle.charAt(0).toUpperCase() + billingCycle.slice(1);
    
    let buttonLabel = "Choose Plan";
    if (plan.id === "enterprise") buttonLabel = "Create Request";
    else if (isCurrentPlan) buttonLabel = "Current Plan";
    else if (isSmallerTier) buttonLabel = "Plan Locked";
    else if (plan.id === "free") buttonLabel = "Free Forever";

    const buttonClass = isCurrentPlan ? "btn-secondary" : "btn-primary";
    const disabledAttr = shouldDisable ? "disabled" : "";

    card.innerHTML = `
            <h3>${plan.name} (${headerCycle})</h3>
            ${priceHtml}
            ${discountText}
            <ul>${featuresHtml}</ul>
            <button class="btn ${buttonClass}" data-plan-id="${plan.id}" data-billing-cycle="${billingCycle}" ${disabledAttr}>
                ${buttonLabel}
            </button>
        `;
    return card;
  }

  // Handle "Choose Plan" button click
  async function handleChoosePlan(event) {
    const planId = event.target.dataset.planId;
    const billingCycle = event.target.dataset.billingCycle;
    const token = localStorage.getItem("access_token");

    if (!token) {
      showToast("Please log in to choose a plan.", "warning");
      setTimeout(() => {
        window.location.href = "login.html";
      }, 1500);
      return;
    }

    if (planId === "enterprise") {
      window.location.href = "create_enterprise_request.html";
      return;
    }

    if (planId === "free") {
      const hasPaidPlan = cachedUserPlan && cachedUserPlan.id !== "free";
      if (hasPaidPlan) {
        showToast(
          "You cannot downgrade to the Free plan while you have an active paid subscription.",
          "error",
        );
        return;
      }

      if (
        !(await showCustomConfirm(
          "Confirm Plan Activation",
          "Are you sure you want to activate the Free (Starter) plan?",
          "info",
        ))
      ) {
        return;
      }
    }

    showGlobalPreloader("Initializing Plan Activation...");
    try {
      const orderResponse = await fetch("/api/subscriptions/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan_id: planId, billing_cycle: billingCycle }),
      });

      const responseData = await orderResponse.json();
      if (!orderResponse.ok) throw new Error(responseData.error || "Failed to initiate subscription.");

      // If it's a free plan or a downgrade to free, the backend returns a success message and potentially a new token
      if (planId === "free" || responseData.message?.toLowerCase().includes("successfully")) {
        showToast(responseData.message || "Plan activated successfully!", "success");
        if (responseData.access_token) {
          localStorage.setItem("access_token", responseData.access_token);
        }
        renderPricingCards();
        hideGlobalPreloader();
        return;
      }

      // For paid plans, proceed with Razorpay
      const keyResponse = await fetch("/api/settings/razorpay-key");
      const keyData = await keyResponse.json();

      // Important: hide the initialization preloader before opening Razorpay
      hideGlobalPreloader();

      const options = {
        key: keyData.key_id,
        amount: responseData.amount,
        currency: "INR",
        name: "LukaIN",
        description: `Subscription: ${planId} (${billingCycle})`,
        order_id: responseData.order_id,
        handler: async function (response) {
          showGlobalPreloader("Verifying Payment...");
          const verifyResponse = await fetch(
            "/api/subscriptions/verify_payment",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                plan_id: planId,
                payment_id: response.razorpay_payment_id,
                order_id: response.razorpay_order_id,
                signature: response.razorpay_signature,
                amount: responseData.amount,
                billing_cycle: billingCycle,
              }),
            },
          );

          if (verifyResponse.ok) {
            const successData = await verifyResponse.json();
            showToast("Subscription activated successfully!", "success");
            localStorage.setItem("access_token", successData.access_token);
            renderPricingCards();
          } else {
            const errorData = await verifyResponse.json();
            showToast(errorData.error || "Payment verification failed", "error");
          }
          hideGlobalPreloader();
        },
        modal: {
          ondismiss: function() {
            hideGlobalPreloader();
          }
        },
        theme: { color: "#066fff" },
      };

      const rzp = new Razorpay(options);
      rzp.open();
    } catch (error) {
      showToast(error.message, "error");
      hideGlobalPreloader();
    }
  }

  renderPricingCards();
  updatePlanDataInBackground(); // Load user details in the background
});
